import React, { useState, Dispatch, useEffect } from 'react';
import { getLocalTime, validateNameLength } from '../../../../lib';
import {
  messageError,
  messageInfo,
  messageSuccess,
} from '../../../../components/message';
import { DeleteGameModal } from './delete-game-modal';
import { useDispatch, useSelector } from 'react-redux';
import {
  GamesAction,
  ReduxGamesActionType,
  getGameList,
} from '../../../../store/modules/games';
import { countStrLength, splitEmail } from '../../../utils';
import { updateGameInfo } from '../../../../api';
import {
  GameSettingInfoLayout,
  IGameSettingItemInfo,
} from '../components/game-setting-info-layout';
import { EditInput } from '../../../studio/game-setting-info-layout/edit-input';
import { IAPIGameItem, IAuditItem } from '../../../../model/api-modules';
import getFormatMessage from '../../../../locales/get-format-message';
import { auditReadLog, auditWriteLog } from '../../../lib/aduit-log';
import { IRootState } from '../../../../store/reducers';

interface IProps {
  readonly selectedGame?: IAPIGameItem | undefined;
  readonly hasPermission: boolean;
  readonly studioID: string | null;
}

export const TabBasic = (props: IProps) => {
  const { hasPermission, selectedGame } = props;
  const gamesDispatch = useDispatch<Dispatch<GamesAction>>();
  const [showNameEdit, setShowNameEdit] = useState<boolean>(false);
  const [showDelModal, setShowDelModal] = useState<boolean>(false);
  const titleName = props.selectedGame?.gameName;
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  const onCloseDelGame = () => {
    setShowDelModal(false);
  };

  useEffect(() => {
    const pathname = location.pathname.split('/');
    const gameInfoIndex = pathname.indexOf('game-info');
    if (gameInfoIndex !== -1) {
      const gameId = pathname[gameInfoIndex + 1];
      auditReadLog(IAuditItem.ViewGameBasicInf, {
        gameID: gameId,
        detail: { gameId },
      });
    }
  }, []);

  const putGameName = async (value: string) => {
    if (value === titleName) {
      setShowNameEdit(false);
      return true;
    }
    if (props.selectedGame) {
      if (countStrLength(value) > 128) {
        messageInfo(getFormatMessage('name_result_can_up_128'));
        return false;
      }
      const res = await updateGameInfo({
        gameID: props.selectedGame.gameID,
        gameName: value,
      });
      auditWriteLog(IAuditItem.EditGameTitle, {
        gameID: props.selectedGame.gameID,
        detail: {
          gameID: props.selectedGame.gameID,
          gameName: value,
        },
      });
      if (res) {
        gamesDispatch({
          type: ReduxGamesActionType.SELECT_GAME,
          result: { ...props.selectedGame, gameName: value },
        });
        // @ts-ignore
        await getGameList(gamesDispatch)();
        setShowNameEdit(false);
        messageSuccess(getFormatMessage('edit_title_name_success'));
        return true;
      }
    }
    messageError(getFormatMessage('edit_title_name_fail'));
    return false;
  };

  const renderDeleteGameModal = () => {
    return showDelModal && props.studioID ? (
      <DeleteGameModal
        selectedGame={props.selectedGame}
        onClose={onCloseDelGame}
        studioID={props.studioID}
      />
    ) : null;
  };

  const renderGameName = () => {
    if (showNameEdit) {
      return (
        <EditInput
          value={selectedGame?.gameName || ''}
          validator={validateNameLength(getFormatMessage('title'))}
          onSubmit={putGameName}
        />
      );
    }
    return selectedGame?.gameName;
  };

  const infoItems: ReadonlyArray<IGameSettingItemInfo> = [
    {
      left: getFormatMessage('title'),
      right: renderGameName(),
      editConfig: {
        disabled: !hasPermission,
        editing: showNameEdit,
        onEdit: () => {
          setShowNameEdit(true);
        },
      },
    },
    {
      left: getFormatMessage('code_name'),
      right: selectedGame?.gameProjectCode || '',
    },
    {
      left: getFormatMessage('title_id'),
      right: selectedGame?.gameID,
      copyConfig: {
        disabled: false,
        text: selectedGame?.gameID || '',
      },
    },
    {
      left: getFormatMessage('title_key'),
      right: '******************',
      className: 'title-key-class',
      copyConfig: {
        disabled: false,
        text: selectedGame?.gameKey || '',
        itemKey: 'titleKey',
      },
    },
    {
      left: getFormatMessage('creator_email'),
      right: props.selectedGame?.gameCreateUserID
        ? `${splitEmail(props.selectedGame?.gameCreateUserID)} (${
            props.selectedGame?.gameCreateUserID
          })`
        : '--',
    },
    {
      left: getFormatMessage('create_time_local'),
      right: selectedGame?.gameCreateTime
        ? getLocalTime(
            selectedGame?.gameCreateTime,
            userInfo?.UserSettings?.Timezone
          )
        : '--',
    },
  ];

  return (
    <>
      <GameSettingInfoLayout
        items={infoItems}
        deleteConfig={{
          description: getFormatMessage('delete_title_desc'),
          disabled: !hasPermission,
          buttonText: getFormatMessage('delete_title'),
          onDelete: () => {
            setShowDelModal(true);
          },
        }}
        selectedGame={selectedGame}
      />
      {renderDeleteGameModal()}
    </>
  );
};
